// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xScreen } from "@adl/XDL/Utilities";
import color from "@adl/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@adl/XDL/Utilities/links";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp, resolveColor } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 5px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 13px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.ui4", theme)};
                color: ${resolveColor("text", theme)};
            }
        }
    `;
};

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.mobile-full-width,
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                    &.relative-row {
                        position: relative;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 125px;
        float: left; // for print only

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
        }

        img {
            display: block;
            max-width: 100%;
            height: auto;
        }
    `,
};

export const createLinkIconStyles = (
    icon: string | React.ComponentType<{}>,
    typographyStyles?: StyledProp,
    iconStyles?: StyledProp,
    iconPosition: "left" | "right" = "left",
) => {
    return {
        typographyProps: {
            size: "12px",
            lineHeight: "13px",
            weight: "bold",
            css: css`
                ${typographyStyles}
            `,
        },
        icon: {
            iconProps: {
                src: icon,
                size: 14,
                css: css`
                    margin-right: 4px;
                    opacity: 0.33;
                    ${iconStyles}
                `,
            },
            position: iconPosition,
        },
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 3px;
        min-width: 3px;
        max-width: 3px;
        height: 3px;
        min-height: 3px;
        max-height: 3px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 10px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        color: ${color("primary.main")};
                        &:hover {
                            text-decoration: underline;
                        }
                    `,
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                    ${IconWrapper} {
                        color: ${color("primary.main", 0.33)};
                    }
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

/**
 *
 * @param rules css rules
 * @returns media query to match safari 10.1+ browsers
 */
export const xSafariOnly = (rules: string) => {
    return `@media not all and (min-resolution:.001dpcm) {
                @supports (-webkit-appearance:none) {
                   ${rules}
                }
            }`;
};

/**
 *
 * @param rules css rules
 * @returns firefox only css styles
 */
export const xFirefoxOnly = (rules: string) => {
    return `@-moz-document url-prefix() { 
        ${rules}
      }`;
};
